import { createAsyncThunk } from '@reduxjs/toolkit';
import { axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { getError } from '@/utils/error-report/getError';
import type { ThunkConfig } from '@/types/store';
import type {
  Annotator,
  AnnotatorTarget,
  AnnotatorPreset,
  AnnotatorPresetsPage,
  AnnotationJob,
  AnnotationJobsPage,
} from '@/types/models';
import {
  annotatorSchema,
  annotatorTargetSchema,
  annotatorPresetsPageSchema,
} from '@/models/annotatorSchema';
import type { RootState } from '../store';
import { apiPath } from '../apiPath';
import {
  ANNOTATORS_FETCHING_ERROR_PREFIX,
  TARGETS_FETCHING_ERROR_PREFIX,
  PRESETS_FETCHING_ERROR_PREFIX,
  PRESET_CREATE_ERROR_PREFIX,
  PRESET_UPDATE_ERROR_PREFIX,
  PRESET_DELETE_ERROR_PREFIX,
  JOBS_FETCHING_ERROR_PREFIX,
  ANNOTATE_PROJECT_ERROR_PREFIX,
  CANCEL_JOB_ERROR_PREFIX,
  JOB_TYPE_NEW_ANNOTATE_PROJECT,
} from './annotators.constants';

export const getAnnotators = createAsyncThunk<Annotator[], void, ThunkConfig>(
  'annotators/getAnnotators',
  async () => {
    try {
      const response = await axiosInstanceNewAPI.get<Annotator[]>(apiPath.getAnnotators());
      const validAnnotators = response.data.filter(item =>
        validateDataUsingZodSchema(item, annotatorSchema),
      );
      return validAnnotators;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: ANNOTATORS_FETCHING_ERROR_PREFIX }));
    }
  },
);

export const getAnnotatorTargets = createAsyncThunk<AnnotatorTarget[], void, ThunkConfig>(
  'annotators/getAnnotatorTargets',
  async () => {
    try {
      const response = await axiosInstanceNewAPI.get<AnnotatorTarget[]>(
        apiPath.getAnnotatorTargets(),
      );
      const validTargets = response.data.filter(item =>
        validateDataUsingZodSchema(item, annotatorTargetSchema),
      );
      return validTargets;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: TARGETS_FETCHING_ERROR_PREFIX }));
    }
  },
);

export const getAnnotatorPresets = createAsyncThunk<AnnotatorPreset[], void, ThunkConfig>(
  'annotators/getAnnotatorPresets',
  async () => {
    try {
      const response = await axiosInstanceNewAPI.get<AnnotatorPresetsPage>(
        apiPath.getAnnotatorPresets(),
      );
      const isValid = validateDataUsingZodSchema(response.data, annotatorPresetsPageSchema);
      return isValid ? response.data.content : [];
    } catch (error) {
      return Promise.reject(getError({ error, prefix: PRESETS_FETCHING_ERROR_PREFIX }));
    }
  },
);

type CreatePresetPayload = {
  name: string;
  annotators: AnnotatorPreset['annotators'];
};

export const createAnnotatorPreset = createAsyncThunk<
  AnnotatorPreset,
  CreatePresetPayload,
  ThunkConfig
>('annotators/createAnnotatorPreset', async payload => {
  try {
    const response = await axiosInstanceNewAPI.post<AnnotatorPreset>(
      apiPath.createAnnotatorPreset(),
      payload,
    );
    return response.data;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: PRESET_CREATE_ERROR_PREFIX }));
  }
});

type UpdatePresetPayload = {
  id: number;
  name: string;
  annotators: AnnotatorPreset['annotators'];
};

export const updateAnnotatorPreset = createAsyncThunk<
  AnnotatorPreset,
  UpdatePresetPayload,
  ThunkConfig
>('annotators/updateAnnotatorPreset', async ({ id, ...payload }) => {
  try {
    const response = await axiosInstanceNewAPI.put<AnnotatorPreset>(
      apiPath.updateAnnotatorPreset(id),
      payload,
    );
    return response.data;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: PRESET_UPDATE_ERROR_PREFIX }));
  }
});

export const deleteAnnotatorPreset = createAsyncThunk<number, number, ThunkConfig>(
  'annotators/deleteAnnotatorPreset',
  async presetId => {
    try {
      await axiosInstanceNewAPI.delete(apiPath.deleteAnnotatorPreset(presetId));
      return presetId;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: PRESET_DELETE_ERROR_PREFIX }));
    }
  },
);

type AnnotateProjectPayload = {
  projectId: string;
  annotatorPresetId: number;
};

export const annotateProject = createAsyncThunk<AnnotationJob, AnnotateProjectPayload, ThunkConfig>(
  'annotators/annotateProject',
  async ({ projectId, annotatorPresetId }) => {
    try {
      const response = await axiosInstanceNewAPI.post<AnnotationJob>(
        apiPath.annotateProject(projectId),
        { annotatorPresetId },
      );
      return response.data;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: ANNOTATE_PROJECT_ERROR_PREFIX }));
    }
  },
);

export const getAllAnnotationJobs = createAsyncThunk<AnnotationJob[], void, ThunkConfig>(
  'annotators/getAllAnnotationJobs',
  async (_, { getState }) => {
    try {
      const state = getState() as RootState;
      const projects = state.projects.data;
      const jobPromises = projects.map(p =>
        axiosInstanceNewAPI
          .get<AnnotationJobsPage>(apiPath.getProjectJobs(p.projectId))
          .then(r => r.data.content)
          .catch(() => [] as AnnotationJob[]),
      );
      const allJobs = await Promise.all(jobPromises);
      return allJobs.flat().filter(job => job.jobType === JOB_TYPE_NEW_ANNOTATE_PROJECT);
    } catch (error) {
      return Promise.reject(getError({ error, prefix: JOBS_FETCHING_ERROR_PREFIX }));
    }
  },
);

export const cancelAnnotationJob = createAsyncThunk<AnnotationJob, number, ThunkConfig>(
  'annotators/cancelAnnotationJob',
  async jobId => {
    try {
      const response = await axiosInstanceNewAPI.delete<AnnotationJob>(apiPath.cancelJob(jobId));
      return response.data;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: CANCEL_JOB_ERROR_PREFIX }));
    }
  },
);
